import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { 
  Bell, BellOff, Smartphone, Check, X, RefreshCw, 
  AlertCircle, Settings, Info, Download
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  isPushSupported,
  subscribeToPush,
  unsubscribeFromPush,
  isPushSubscribed,
  getNotificationPermission,
  getNotificationPreferences,
  updateNotificationPreferences,
  sendTestNotification
} from '../utils/pushNotifications';

const NOTIFICATION_LABELS = {
  nouvel_appel_fonds: { label: "Nouvel appel de fonds", description: "Quand un nouvel appel de fonds est émis" },
  rappel_paiement: { label: "Rappel de paiement", description: "Quand un rappel de paiement est envoyé" },
  nouvelle_ag: { label: "Assemblée générale", description: "Quand une nouvelle AG est programmée" },
  nouveau_message: { label: "Nouveau message", description: "Quand vous recevez un nouveau message" },
  nouvel_incident: { label: "Nouvel incident", description: "Quand un incident est signalé" },
  alerte_contrat: { label: "Alerte contrat", description: "Quand un contrat arrive à échéance" },
  nouveau_sinistre: { label: "Nouveau sinistre", description: "Quand un sinistre est déclaré" }
};

const NotificationSettings = () => {
  const { token } = useAuth();
  const [isSupported, setIsSupported] = useState(true);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [permission, setPermission] = useState('default');
  const [preferences, setPreferences] = useState({});
  const [availableTypes, setAvailableTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);

  // Check PWA installation status
  useEffect(() => {
    const checkInstallation = () => {
      // Check if running as PWA
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
      const isIOSStandalone = window.navigator.standalone === true;
      setIsInstalled(isStandalone || isIOSStandalone);
    };
    
    checkInstallation();

    // Listen for beforeinstallprompt event
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    window.addEventListener('appinstalled', () => setIsInstalled(true));

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  const checkStatus = useCallback(async () => {
    setIsSupported(isPushSupported());
    setPermission(getNotificationPermission());
    
    if (isPushSupported()) {
      const subscribed = await isPushSubscribed();
      setIsSubscribed(subscribed);
    }
  }, []);

  const loadPreferences = useCallback(async () => {
    const data = await getNotificationPreferences(token);
    if (data) {
      setPreferences(data.preferences || {});
      setAvailableTypes(data.available_types || []);
    }
    setLoading(false);
  }, [token]);

  useEffect(() => {
    checkStatus();
    loadPreferences();
  }, [checkStatus, loadPreferences]);

  const handleInstallPWA = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setIsInstalled(true);
        toast.success('Application installée !');
      }
      setDeferredPrompt(null);
    }
  };

  const handleSubscribe = async () => {
    setSubscribing(true);
    const result = await subscribeToPush(token);
    
    if (result.success) {
      setIsSubscribed(true);
      toast.success('Notifications activées !');
    } else {
      switch (result.reason) {
        case 'permission_denied':
          toast.error('Vous avez refusé les notifications. Activez-les dans les paramètres de votre navigateur.');
          break;
        case 'not_supported':
          toast.error('Les notifications ne sont pas supportées sur ce navigateur.');
          break;
        default:
          toast.error('Erreur lors de l\'activation des notifications');
      }
    }
    
    setSubscribing(false);
    checkStatus();
  };

  const handleUnsubscribe = async () => {
    const success = await unsubscribeFromPush(token);
    if (success) {
      setIsSubscribed(false);
      toast.success('Notifications désactivées');
    }
    checkStatus();
  };

  const handlePreferenceChange = async (key, value) => {
    const newPrefs = { ...preferences, [key]: value };
    setPreferences(newPrefs);
    
    const success = await updateNotificationPreferences(token, { [key]: value });
    if (!success) {
      // Revert on error
      setPreferences(preferences);
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleSendTest = async () => {
    setSendingTest(true);
    const result = await sendTestNotification(token);
    
    if (result?.status === 'sent') {
      toast.success('Notification de test envoyée !');
    } else if (result?.status === 'no_subscription') {
      toast.warning('Activez d\'abord les notifications');
    } else {
      toast.error('Erreur lors de l\'envoi');
    }
    
    setSendingTest(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-40">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="notification-settings">
      {/* PWA Installation Section */}
      <div className="bg-white rounded-xl p-6 shadow-sm border">
        <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Smartphone className="w-5 h-5 text-teal-500" />
          Application mobile
        </h3>
        
        {isInstalled ? (
          <div className="flex items-center gap-3 p-4 bg-green-50 rounded-lg border border-green-100">
            <Check className="w-6 h-6 text-green-600" />
            <div>
              <p className="font-medium text-green-800">Application installée</p>
              <p className="text-sm text-green-600">SyndicAppli est installé sur votre appareil</p>
            </div>
          </div>
        ) : deferredPrompt ? (
          <div className="space-y-3">
            <p className="text-gray-600 text-sm">
              Installez SyndicAppli sur votre appareil pour un accès rapide et des notifications même lorsque le navigateur est fermé.
            </p>
            <Button onClick={handleInstallPWA} className="bg-teal-500 hover:bg-teal-600">
              <Download className="w-4 h-4 mr-2" />
              Installer l'application
            </Button>
          </div>
        ) : (
          <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-lg border border-blue-100">
            <Info className="w-5 h-5 text-blue-600" />
            <div>
              <p className="text-sm text-blue-700">
                Sur iOS, utilisez le bouton <strong>Partager</strong> puis <strong>"Sur l'écran d'accueil"</strong>.
                Sur Android, utilisez le menu du navigateur.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Push Notifications Section */}
      <div className="bg-white rounded-xl p-6 shadow-sm border">
        <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Bell className="w-5 h-5 text-teal-500" />
          Notifications push
        </h3>

        {!isSupported ? (
          <div className="flex items-center gap-3 p-4 bg-yellow-50 rounded-lg border border-yellow-100">
            <AlertCircle className="w-6 h-6 text-yellow-600" />
            <div>
              <p className="font-medium text-yellow-800">Non supporté</p>
              <p className="text-sm text-yellow-600">Votre navigateur ne supporte pas les notifications push</p>
            </div>
          </div>
        ) : permission === 'denied' ? (
          <div className="flex items-center gap-3 p-4 bg-red-50 rounded-lg border border-red-100">
            <BellOff className="w-6 h-6 text-red-600" />
            <div>
              <p className="font-medium text-red-800">Notifications bloquées</p>
              <p className="text-sm text-red-600">
                Les notifications sont bloquées. Modifiez les paramètres de votre navigateur pour les autoriser.
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-gray-900">Activer les notifications</p>
                <p className="text-sm text-gray-500">Recevez des alertes en temps réel</p>
              </div>
              {isSubscribed ? (
                <div className="flex items-center gap-2">
                  <Badge className="bg-green-100 text-green-700">Activé</Badge>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={handleUnsubscribe}
                    className="text-red-600 border-red-200 hover:bg-red-50"
                  >
                    Désactiver
                  </Button>
                </div>
              ) : (
                <Button 
                  onClick={handleSubscribe}
                  disabled={subscribing}
                  className="bg-teal-500 hover:bg-teal-600"
                >
                  {subscribing ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Bell className="w-4 h-4 mr-2" />
                  )}
                  Activer
                </Button>
              )}
            </div>

            {isSubscribed && (
              <Button 
                variant="outline" 
                size="sm"
                onClick={handleSendTest}
                disabled={sendingTest}
              >
                {sendingTest ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Bell className="w-4 h-4 mr-2" />
                )}
                Envoyer une notification test
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Notification Preferences */}
      {isSubscribed && availableTypes.length > 0 && (
        <div className="bg-white rounded-xl p-6 shadow-sm border">
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Settings className="w-5 h-5 text-teal-500" />
            Préférences de notification
          </h3>
          
          <div className="space-y-4">
            {availableTypes.map(type => {
              const config = NOTIFICATION_LABELS[type];
              if (!config) return null;
              
              return (
                <div key={type} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                  <div>
                    <p className="font-medium text-gray-900">{config.label}</p>
                    <p className="text-sm text-gray-500">{config.description}</p>
                  </div>
                  <Switch
                    checked={preferences[type] !== false}
                    onCheckedChange={(checked) => handlePreferenceChange(type, checked)}
                  />
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationSettings;
